/* eslint-disable no-param-reassign */
/* eslint-disable no-plusplus */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import { isNumber } from 'lodash'
import { useMemo } from 'react'
import { useGetCustomerSecurityPosture } from './secure'
import prepareHeaders from '../utils/prepareHeaders'
import { getSelectedCustomerId } from '../selectors/customers'

export const milestonesApi = createApi({
  reducerPath: 'milestones',
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getTemplates: builder.query({
      query: (customerId = '*') => `/secure/milestones/${customerId}`,
    }),
  }),
})

export const { useGetTemplatesQuery, useGetTemplateItemsQuery } = milestonesApi

export const useGetCustomerTemplates = () => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetTemplatesQuery(customerId)
}

export const useGetCustomerTemplateCompliance = () => {
  const { data: templates = [] } = useGetCustomerTemplates()
  const { data: securityPosture } = useGetCustomerSecurityPosture()

  return useMemo(() => {
    if (!securityPosture) {
      return {}
    }

    const { securityChecks: { allMonitored = [], ignored = [] } = {} } = securityPosture

    return {
      data: templates.reduce(
        (result, { id, checks }) =>
          checks.length
            ? {
                ...result,
                [id]: checks.reduce(
                  (complianceResult, check) => {
                    const { compliance } = [...allMonitored, ...ignored].find((item) => item.checkId === check) || {}

                    if (compliance) {
                      const { compliant, total } = compliance

                      if (compliant === true) {
                        complianceResult.compliant++
                      }

                      if (compliant === false) {
                        complianceResult.nonCompliant++
                      }

                      if (isNumber(compliant)) {
                        if (compliant === total) {
                          complianceResult.compliant++
                        } else {
                          complianceResult.partial++
                          complianceResult.compliant += 0.5
                        }
                      }
                    }

                    return complianceResult
                  },
                  {
                    total: checks.length,
                    compliant: 0,
                    partial: 0,
                    nonCompliant: 0,
                  },
                ),
              }
            : result,
        {},
      ),
    }
  }, [templates, securityPosture])
}

export const useGetCustomerTemplateChecks = (templateId) => {
  const { data: securityPosture } = useGetCustomerSecurityPosture()
  const { data: templates = [], isLoading } = useGetCustomerTemplates()

  return useMemo(() => {
    if (!securityPosture || !templateId || isLoading) {
      return { isLoading: true }
    }

    const { securityChecks: { allMonitored = [], ignored = [] } = {} } = securityPosture
    const { checks = [] } = templates.find(({ id }) => id === templateId) ?? {}

    return {
      data: [...allMonitored, ...ignored].filter(({ checkId }) => checks.includes(checkId)),
    }
  }, [securityPosture, templateId, templates, isLoading])
}
